import ReactECharts from 'echarts-for-react';
import { useIsNarrow } from '@/components/blog/useIsNarrow';
import { useIsDark } from '../../useIsDark';

/**
 * EmailReplyRateDeltaChart — blog18, Figure 2: reply rate by sending condition.
 *
 * Chart-type-first: three paired before/after conditions on one shared percent
 * axis → horizontal grouped bars (paired rows), not a single series, since the
 * point is the WITHIN-PAIR delta (tracking on/off, freemail/custom domain),
 * each pair independently sourced. A stacked or single-series bar would hide
 * the pairing that is the actual finding.
 *
 * Data: raisy/projects/blog/assets/blog18/raisy_email_format_deliverability_data.json
 * Source: Hunter.io, "State of Cold Email 2026" report — 31 million emails
 * sent by Hunter.io users in 2025 (hunter.io/the-state-of-cold-email).
 * Correlational aggregate data, not a controlled single-message experiment —
 * stated in the post body and in the caption below (Rule 36).
 *
 * Opaque background; ONE shared percent formatter for bars, labels, tooltip.
 */

const BRAND = {
  teal: '#0AB3BF',
  tealSoft: '#99E2E8',
  muted: '#64748B',
  mutedSoft: '#B6C2CF',
};

type Pair = {
  label: string;
  baseline: { name: string; value: number };
  better: { name: string; value: number };
  relativeLift: string;
};

const PAIRS: Pair[] = [
  {
    label: 'Open-tracking pixel',
    baseline: { name: 'Tracking enabled', value: 4.4 },
    better: { name: 'No tracking pixel', value: 7.4 },
    relativeLift: '+68%',
  },
  {
    label: 'Sending domain',
    baseline: { name: 'Freemail address', value: 2.5 },
    better: { name: 'Custom domain', value: 5.2 },
    relativeLift: '+108%',
  },
  {
    label: 'Batch size',
    baseline: { name: '500+ recipients', value: 2.4 },
    better: { name: '21–50 recipients', value: 6.2 },
    relativeLift: '+158%',
  },
];

/** ONE shared formatter — bar labels, axis, and tooltip all speak in percent. */
const fmtPct = (v: number) => `${v}%`;

export const EmailReplyRateDeltaChart = ({
  height,
  dark,
}: {
  height?: number;
  dark?: boolean;
}) => {
  const narrow = useIsNarrow();
  const liveDark = useIsDark();
  const isDark = dark ?? liveDark;

  const C = isDark
    ? { bg: '#0F172A', axis: '#E2E8F0', muted: '#94A3B8', grid: '#334155', card: 'bg-slate-900', border: 'border-slate-800', caption: 'text-slate-400' }
    : { bg: '#FFFFFF', axis: '#334155', muted: '#64748B', grid: '#334155', card: 'bg-white', border: 'border-slate-200', caption: 'text-slate-500' };

  const categories = PAIRS.flatMap((p) => [p.better.name, p.baseline.name]);
  const values = PAIRS.flatMap((p) => [p.better.value, p.baseline.value]);
  const colors = PAIRS.flatMap(() => [BRAND.teal, BRAND.mutedSoft]);

  const chartHeight = height ?? (narrow ? 340 : 300);
  const gutter = narrow ? 132 : 168;

  const option = {
    backgroundColor: C.bg,
    grid: { left: gutter, right: narrow ? 44 : 64, top: 24, bottom: 24, containLabel: false },
    tooltip: {
      trigger: 'item',
      backgroundColor: '#1E293B',
      borderColor: '#334155',
      textStyle: { color: '#F8FAFC', fontSize: 12 },
      formatter: (p: any) => {
        const pairIdx = Math.floor(p.dataIndex / 2);
        const pair = PAIRS[pairIdx];
        return `<b>${categories[p.dataIndex]}</b>: ${fmtPct(values[p.dataIndex])} reply rate<br/><span style="opacity:0.75">${pair.label} — relative lift ${pair.relativeLift}</span>`;
      },
    },
    xAxis: {
      type: 'value',
      name: 'Reply rate',
      nameLocation: 'end',
      nameTextStyle: { color: C.muted, fontSize: 11 },
      axisLabel: { color: C.muted, fontSize: 11, formatter: fmtPct },
      axisLine: { lineStyle: { color: C.grid } },
      splitLine: { lineStyle: { color: C.grid, opacity: 0.3 } },
    },
    yAxis: {
      type: 'category',
      data: categories,
      inverse: true,
      axisLabel: { color: C.axis, fontSize: narrow ? 10.5 : 12 },
      axisLine: { lineStyle: { color: C.grid } },
      axisTick: { show: false },
    },
    series: [
      {
        type: 'bar',
        data: values.map((v, i) => ({ value: v, itemStyle: { color: colors[i], borderRadius: [0, 4, 4, 0] } })),
        barWidth: narrow ? 14 : 18,
        barCategoryGap: '35%',
        label: {
          show: true,
          position: 'right',
          color: C.axis,
          fontSize: 11,
          fontWeight: 700,
          formatter: (p: any) => fmtPct(p.value),
        },
      },
    ],
  };

  return (
    <div className={`rounded-xl border ${C.border} ${C.card} p-3`}>
      <ReactECharts option={option} style={{ height: chartHeight, width: '100%' }} opts={{ renderer: 'svg' }} />
      <p className={`mt-2 text-xs ${C.caption}`}>
        Reply rate by sending condition, paired (teal = the higher-reply-rate condition in each pair). Aggregate correlational data across 31 million
        emails sent by Hunter.io users in 2025 — not a controlled single-message experiment. Source: Hunter.io, "State of Cold Email 2026" report.
      </p>
    </div>
  );
};
